/**
 * Storage abstraction — localStorage on web, Keychain-backed
 * @capacitor/preferences on iOS/Android.
 *
 * Two ways to use this:
 *
 * 1. `storage.getItem/setItem/removeItem` — a plain async interface for
 *    anything that reads/writes from an effect, an event handler, or an
 *    already-async function. Just add `await`.
 *
 * 2. `readSync/writeSync/clearSync` — for the handful of keys (auth token,
 *    auth user, the remembered in-app return path) that are read
 *    SYNCHRONOUSLY today: the axios request interceptor (fires on every API
 *    call, including ones that can go out before any React effect has run),
 *    streamFetch, and a couple of useMemo/lazy-useState reads during render.
 *    @capacitor/preferences has no synchronous API, so these keys are kept
 *    in an in-memory cache instead — the single read path once hydrated.
 *
 *    On web the cache is seeded synchronously at import time straight from
 *    localStorage, so those call sites behave exactly as before this file
 *    existed — no timing change, no behaviour change. On native there's
 *    nothing to read synchronously at import time, so the cache starts empty
 *    and `hydrateSyncCache()` must be awaited once, before the app renders
 *    (see main.jsx) — after that, native behaves the same as web: a
 *    synchronous read, with writes updating memory immediately and
 *    persisting to Preferences in the background.
 */

import { Preferences } from '@capacitor/preferences'
import { IS_NATIVE_APP } from './runtime'

const isNative = IS_NATIVE_APP

function readLocalStorage(key) {
  try {
    return typeof window === 'undefined' ? null : window.localStorage.getItem(key)
  } catch {
    // Private mode / storage blocked — treat as empty rather than throwing.
    return null
  }
}

async function rawGet(key) {
  if (isNative) {
    const { value } = await Preferences.get({ key })
    return value
  }
  return readLocalStorage(key)
}

async function rawSet(key, value) {
  if (isNative) {
    await Preferences.set({ key, value })
    return
  }
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Storage can be unavailable (private mode, quota). Callers already
    // treat persistence as best-effort for these keys.
  }
}

async function rawRemove(key) {
  if (isNative) {
    await Preferences.remove({ key })
    return
  }
  try {
    window.localStorage.removeItem(key)
  } catch {
    // See rawSet.
  }
}

export const storage = {
  isNative: () => isNative,
  getItem: rawGet,
  setItem: rawSet,
  removeItem: rawRemove,
}

// ── Sync-read cache ─────────────────────────────────────────────────────
const SYNC_KEYS = ['jai_token', 'jai_user', 'jai_last_app_path']

const cache = Object.fromEntries(
  SYNC_KEYS.map((key) => [key, isNative ? null : readLocalStorage(key)])
)

let hydrated = !isNative

/** Awaited once at startup (main.jsx) before the app renders. No-op on web
 *  — the cache is already seeded synchronously above. */
export async function hydrateSyncCache() {
  if (hydrated) return
  const values = await Promise.all(SYNC_KEYS.map(rawGet))
  SYNC_KEYS.forEach((key, i) => { cache[key] = values[i] })
  hydrated = true
}

export function readSync(key) {
  return cache[key]
}

export function writeSync(key, value) {
  cache[key] = value
  rawSet(key, value).catch((err) => console.error(`[storage] failed to persist ${key}`, err))
}

export function clearSync(key) {
  cache[key] = null
  rawRemove(key).catch((err) => console.error(`[storage] failed to clear ${key}`, err))
}
